<template>
  <div class="container mt-4">
    <div class="row mt-4">
      <div class="col h1 mb-md-4 mt-4">
        {{ $t('STUDIO.TITLE.MY_SETTINGS') }}
      </div>
    </div>
    <hr class="my-4" />
    <div class="row">
      <div class="col">
        <HMHeadingWithButton
          :headingText="$t('STUDIO.TITLE.CONTACT_INFORMATION')"
          :buttonText="$t('STUDIO.BUTTON.EDIT_CONTACT_INFO')"
          icon="far fa-pencil-alt"
          @click="openContactForm"
        />
        <div v-if="user">{{ studentInfoShorthand }}</div>
      </div>
    </div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <form
        v-if="contactFormOpen"
        @submit.prevent="updateStudentInfo"
        class="mt-3"
      >
        <div class="bg-lightest-gray col rounded p-4" v-if="shown">
          <div class="row align-items-center">
            <div class="col-5">
              <h3>{{ $t('STUDIO.TITLE.UPLOAD_PROFILE_PIC') }}</h3>
              <p>{{ $t('STUDIO.TEXT.IMAGE_MUST_BE') }}</p>
              <HMFileUpload
                :enableDeleteFile="true"
                accept="image/*"
                class="pt-3"
              >
                <HMButton color="outline-gray">
                  <span class="far fa-cloud-upload pr-2 c-coral my-2"></span>
                  {{ $t('STUDIO.BUTTON.UPLOAD_PHOTO') }}
                </HMButton>
              </HMFileUpload>
            </div>
            <div class="col-7">
              <img
                src="/assets/images/default-instructor-upload.svg"
                class="m-4 float-right"
                height="200px"
              />
            </div>
          </div>
        </div>
        <div class="bg-lightest-gray col rounded p-4">
          <div class="row">
            <div class="col-md-6">
              <HMTextInput
                :label="$t('STUDIO.LABELS.FIRST_NAME')"
                rules="required"
                v-model="contactFormModel.first_name"
              />
            </div>
            <div class="col-md-6">
              <HMTextInput
                :label="$t('STUDIO.LABELS.LAST_NAME')"
                rules="required"
                v-model="contactFormModel.last_name"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <HMTextInput
                :label="$t('STUDIO.LABELS.ADDRESS')"
                v-model="contactFormModel.address"
              />
            </div>
            <div class="col-md-6">
              <HMTextInput
                :label="$t('SETTINGS.LABEL.CITY')"
                v-model="contactFormModel.city"
              />
            </div>
            <div class="col-md-4">
              <HMTextInput
                :label="$t('SETTINGS.LABEL.ZIP_CODE')"
                v-model="contactFormModel.zipcode"
              />
            </div>
            <div class="col-md-4">
              <HMTextInput
                :label="$t('SETTINGS.LABEL.STATE_PROVINCE')"
                v-model="contactFormModel.state"
              />
            </div>
            <div class="col-md-4">
              <HMTextInput
                :label="$t('SETTINGS.LABEL.COUNTRY')"
                v-model="contactFormModel.country"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <HMTextInput
                :label="$t('STUDIO.LABELS.PHONE')"
                v-model="contactFormModel.phone"
                :disabled="true"
              />
            </div>
            <div class="col-md-6">
              <HMTextInput
                :label="$t('STUDIO.LABELS.EMAIL')"
                v-model.trim="studentEmail"
                rules="required"
                :disabled="true"
              />
            </div>
          </div>
          <div class="row">
            <div class="col mb-3">
              <HMButton
                color="outline-gray"
                type="button"
                @click="closeContactForm"
              >
                {{ $t('GENERAL.BUTTON.DISCARD') }}
              </HMButton>
              <div class="float-right">
                <HMButton
                  class="btn bg-coral c-white"
                  :loading="contactFormInProgress"
                  :disabled="invalid"
                  type="submit"
                >
                  {{ $t('GENERAL.BUTTON.SAVE_CHANGES') }}
                </HMButton>
              </div>
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
    <section v-if="contactFormOpen">
      <hr />
      <StudentPhoneNumberComponent></StudentPhoneNumberComponent>
    </section>
    <hr class="my-4" />
    <div v-if="getLength(signedContracts)">
      <div class="row">
        <div class="col col-md-6 h2">
          {{ $t('STUDIO.TITLE.CONTRACTS') }}
        </div>
      </div>

      <div class="table-wrap">
        <table class="table mb-0">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('CONTRACTS.TABLE.CONTRACT_TITLE') }}
              </th>
              <th scope="col">
                {{ $t('CONTRACTS.TABLE.STATUS') }}
              </th>
              <th scope="col">
                {{ $t('CONTRACTS.TABLE.SIGNED_ON') }}
              </th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tr
            v-for="signedContract in signedContracts"
            :key="signedContract.id"
          >
            <td class="p m-0">
              <router-link
                :to="{
                  name: 'studio-sign-contract',
                  params: { unique_id: signedContract.unique_id },
                }"
              >
                {{ signedContract.contract.title }}
              </router-link>
            </td>
            <td>
              <div
                class="d-inline-flex justify-content-start align-items-center flex-nowrap"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle']"
                  :class="`dot dot--${
                    !!signedContract.document ? 'signed' : 'pending'
                  }`"
                />
                <span class="c-charcoal">{{
                  !!signedContract.document
                    ? $t('CONTRACTS.LABELS.SIGNED')
                    : $t('CONTRACTS.LABELS.PENDING')
                }}</span>
              </div>
            </td>
            <td>
              {{ $formatDate(signedContract.signed, 'date-time', '-') }}
            </td>
            <td>
              <a
                :href="signedContract.document.storage_url"
                v-if="signedContract.document"
                target="_blank"
                download
              >
                {{ $t('CONTRACTS.LABELS.DOWNLOAD') }}
              </a>
            </td>
          </tr>
        </table>
      </div>
      <hr class="my-4" />
    </div>
    <div v-if="getLength(currentStudioSubscriptions) || getLength(purchases)">
      <div class="row">
        <div class="col col-md-6 h2">
          {{ $t('STUDIO.TITLE.SUBSCRIPTIONS') }}
        </div>
        <div class="col-auto col-md-6 align-self-center">
          <NavItem
            class="float-right"
            icon="far fa-file-invoice"
            :text="$t('STUDIO.BUTTON.VIEW_PAST_PURCHASES')"
            :iconOnRight="true"
            @click="$router.push({ name: 'student-purchase-history' })"
          />
          <NavItem
            v-if="showUpdatePaymentInfo"
            class="float-right"
            icon="far fa-credit-card"
            :text="$t('STUDIO.BUTTON.UPDATE_PAYMENT_INFO')"
            :iconOnRight="true"
            @click="updatePaymentInfo"
          />
        </div>
      </div>

      <div class="table-wrap">
        <table class="table mb-0">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('STUDIO.LABELS.PRODUCT') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.PURCHASE_DATE') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.PRODUCT_PRICE') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.LAST_PAID_AMOUNT') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.RENEWAL') }}
              </th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tr
            v-for="subscription in currentStudioSubscriptions"
            :key="subscription.id"
          >
            <td
              class="p m-0 cursor-pointer"
              @click="
                $router.push({
                  name: 'studio-product',
                  params: {
                    studio_url: subscription.studio_url,
                    product_id: subscription.product_id,
                  },
                })
              "
            >
              <div class="d-flex">
                <span class="h5 pr-1 product-name mb-0">
                  {{ subscription.product_name }}
                </span>
              </div>
              <div class="small">{{ subscription.studio_name }}</div>
            </td>
            <td>
              {{ $formatDate(subscription.purchase_time) }}
            </td>
            <td>
              {{
                $formatCurrency(
                  subscription.product_price,
                  subscription.currency
                )
              }}
            </td>
            <td>
              {{
                $formatCurrency(subscription.amount_paid, subscription.currency)
              }}
            </td>
            <td>
              <div
                :class="{
                  cancelled: subscription.canceled || subscription.paused,
                }"
              >
                {{ $formatDate(subscription.renewal_date) }}
              </div>
              <div
                :class="`small status--${getSubscriptionState(subscription)}`"
              >
                {{ getSubscriptionState(subscription) }}
              </div>
            </td>
            <td>
              <HMButton
                color="brand"
                v-if="
                  !subscription.canceled &&
                  subscription.subscription_type === 'stripe'
                "
                v-confirm="{
                  title: $t('STUDIO.TITLE.CANCEL_SUBSCRIPTION'),
                  text: $t('STUDIO.TEXT.CANCEL_SUBSCRIPTION_TEXT'),
                  confirmButton: $t('STUDIO.BUTTON.CANCEL_SUBSCRIPTION'),
                  confirm: () => cancelSubscription(subscription.id),
                }"
              >
                {{ $t('SETTINGS.BUTTON.CANCEL') }}
              </HMButton>
              <HMButton
                v-if="
                  !subscription.canceled &&
                  subscription.subscription_type === 'paypal'
                "
                icon="far fa-trash-alt"
                v-confirm="{
                  title: $t('STUDIO.TITLE.CANCEL_SUBSCRIPTION'),
                  text: $t('STUDIO.TEXT.CANCEL_SUBSCRIPTION_TEXT_PAYPAL'),
                  confirmButton: $t('STUDIO.BUTTON.MORE_INFORMATION'),
                  confirm: () => showPayPalInstructions(),
                }"
              >
                {{ $t('SETTINGS.BUTTON.CANCEL') }}
              </HMButton>
            </td>
          </tr>
        </table>
      </div>
      <hr class="my-4" />
    </div>
    <div v-if="getLength(getActivePunches)">
      <HMHeadingWithButton
        :headingText="$t('STUDIO.TITLE.AVAILABLE_REDEMPTIONS')"
        icon="far fa-pencil"
        :buttonText="$t('STUDIO.BUTTON.VIEW_ALL_REDEMPTIONS')"
        @click="$router.push({ name: 'student-redemptions' })"
      />
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('STUDIO.LABELS.STUDIO') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.AVAILABLE') }}
              </th>
              <th scope="col">
                {{ $t('STUDIO.LABELS.EXPIRATION_DATE') }}
              </th>
            </tr>
          </thead>
          <tr v-for="punch in getActivePunches" :key="punch.id">
            <td class="p m-0">
              {{ punch.studio_name }}
            </td>
            <td>
              {{ punch.number_of_punches }}
            </td>
            <td>
              {{ $formatDate(punch.expiration) }}
            </td>
          </tr>
        </table>
      </div>
      <hr class="my-4" />
    </div>
    <div v-if="shown">
      <HMHeadingWithButton
        :headingText="$t('STUDIO.TITLE.BILLING_CARD')"
        :buttonText="$t('STUDIO.BUTTON.UPDATE_PAYMENT_METHOD')"
        icon="far fa-arrow-right"
      />
      <hr class="my-4" />
    </div>
    <div class="row">
      <div class="col h2">
        {{ $t('STUDIO.TITLE.TIMEZONE_SETTINGS') }}
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col" v-if="user">
        <v-select
          :options="timezones"
          label="name"
          code="value"
          v-model="user.timezone"
          @input="setTimezone"
        >
        </v-select>
      </div>
    </div>
    <!--
    <hr class="my-4" />
    <div class="row">
      <div class="col h2">
        {{ $t('SETTINGS.TITLE.LANGUAGE_SETTINGS') }}
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col" v-if="user">
        <v-select
          :options="languages"
          label="name"
          :value="localLanguage"
          @input="setLanguage"
        >
        </v-select>
      </div>
    </div>
    -->
    <hr class="mt-4 mb-4" />
    <div class="row">
      <div class="col">
        <HMHeadingWithButton
          :headingText="$t('STUDIO.TITLE.NOTIFICATIONS')"
          :buttonText="$t('STUDIO.BUTTON.CHANGE')"
          icon="far fa-pencil-alt"
          @click="openNotificationForm"
        />
        <div class="mb-3">{{ $t('STUDIO.TEXT.NOTIFICATIONS') }}</div>
      </div>
    </div>
    <div class="bg-lightest-gray rounded col p-4" v-if="notificationFormOpen">
      <div
        class="row d-flex flex-row flex-nowrap align-items-center justify-content-between"
      >
        <div class="col-6">
          <h4>{{ $t('STUDIO.LABELS.IN_APP_NOTIFICATIONS') }}</h4>
        </div>
        <div class="col-6 align-self-center">
          <div class="float-right pb-2" v-if="user">
            <HMSwitch v-model="eventNotificationsEnabled" />
          </div>
        </div>
      </div>
      <div
        class="row d-flex flex-row flex-nowrap align-items-center justify-content-between mt-2"
      >
        <div class="col-6">
          <h4>{{ $t('STUDIO.LABELS.SMS_NOTIFICATIONS') }}</h4>
        </div>
        <div class="col-6 align-self-center">
          <div class="float-right" v-if="user">
            <HMSwitch v-model="smsNotificationsEnabled" />
          </div>
        </div>
      </div>
      <div
        class="row d-flex flex-row flex-nowrap align-items-center justify-content-between mt-2"
      >
        <div class="col-6">
          <h4>{{ $t('STUDIO.LABELS.EMAIL_NOTIFICATIONS') }}</h4>
        </div>
        <div class="col-6 align-self-center">
          <div class="float-right" v-if="user">
            <HMSwitch v-model="emailNotificationsEnabled" />
          </div>
        </div>
      </div>
      <div class="row mt-2" v-if="emailNotificationsEnabled">
        <div class="col-12 my-3">
          <HMCheckBox v-model="emailNotifications30minEnabled">
            {{ $t('STUDIO.LABELS.30_MIN_EMAIL') }}
          </HMCheckBox>
        </div>
        <div class="col-12 my-3">
          <HMCheckBox v-model="emailNotifications1hrEnabled">
            {{ $t('STUDIO.LABELS.1_HR_EMAIL') }}
          </HMCheckBox>
        </div>
        <div class="col-12 my-3">
          <HMCheckBox v-model="privatePodcastNotificationsEmailEnabled">
            {{ $t('STUDIO.LABELS.PRIVATE_PODCAST_EPISODE_NOTIFICATIONS') }}
          </HMCheckBox>
        </div>
        <div class="col-12 my-3">
          <HMCheckBox v-model="communityDailyDigestEmailEnabled">
            {{ $t('STUDIO.LABELS.COMMUNITY_DIGEST_EMAIL') }}
          </HMCheckBox>
        </div>
        <div class="col-12 my-3">
          <HMCheckBox v-model="communityChatNotificationsEmailEnabled">
            {{ $t('STUDIO.LABELS.COMMUNITY_CHAT_MESSAGE_EMAIL') }}
          </HMCheckBox>
        </div>
        <div class="col-12 my-3">
          <HMCheckBox v-model="communityRealtimeMentionsEmailEnabled">
            {{ $t('STUDIO.LABELS.COMMUNITY_MENTION_EMAIL') }}
          </HMCheckBox>
        </div>
      </div>
      <hr class="my-4" />
      <HMButton
        color="outline-gray"
        type="button"
        @click="closeNotificationForm"
      >
        {{ $t('STUDIO.BUTTON.CLOSE') }}
      </HMButton>
    </div>
    <hr class="mt-4 mb-4" />
    <div class="row">
      <div class="col">
        <HMHeadingWithButton
          :headingText="$t('STUDIO.TITLE.CHANGE_PASSWORD')"
          :buttonText="$t('STUDIO.BUTTON.CHANGE_PASSWORD')"
          icon="far fa-pencil-alt"
          @click="openPasswordForm"
        />
      </div>
    </div>
    <div
      class="bg-lightest-gray col rounded mt-1 mb-4 p-4"
      v-if="passwordFormOpen"
    >
      <form @submit.prevent="updatePassword">
        <div class="row">
          <div class="col-md-6">
            <HMTextInput
              :label="$t('STUDIO.LABELS.NEW_PASSWORD_1')"
              v-model="newPassword1"
              type="password"
            />
          </div>
          <div class="col-md-6">
            <HMTextInput
              :label="$t('STUDIO.LABELS.NEW_PASSWORD_2')"
              v-model="newPassword2"
              type="password"
            />
          </div>
        </div>
        <div class="invalid-feedback d-block mb-2" v-show="passwordFormError">
          {{ passwordFormError }}
        </div>
        <div class="row">
          <div class="col">
            <HMButton
              color="outline-gray"
              type="button"
              @click="closePasswordForm"
            >
              {{ $t('GENERAL.BUTTON.DISCARD') }}
            </HMButton>
            <div class="float-right">
              <HMButton
                class="btn bg-coral c-white"
                :loading="passwordInProgress"
                type="submit"
              >
                {{ $t('GENERAL.BUTTON.SAVE_CHANGES') }}
              </HMButton>
            </div>
          </div>
        </div>
      </form>
    </div>
    <hr class="mt-4 mb-4" />
    <div class="row" v-if="shown">
      <div class="col">
        <HMHeadingWithButton
          :headingText="$t('STUDIO.TITLE.DELETE_ACCOUNT')"
          :buttonText="$t('STUDIO.BUTTON.DELETE_ACCOUNT')"
          icon="far fa-trash-alt"
          v-confirm="{
            title: $t('STUDIO.TITLE.DELETE_ACCOUNT'),
            text: $t('STUDIO.TEXT.THIS_IS_GOING_TO_BE_FINAL_PLEASE'),
            confirmButton: $t('STUDIO.BUTTON.DELETE_ACCOUNT'),
            confirm: () => cancelAccount(),
          }"
        />
        {{ $t('STUDIO.TEXT.DELETE_ACCOUNT') }}
      </div>
    </div>
    <AddToHomeScreen
      class="d-md-none"
      :studioName="studio.studio_name"
      :appIcon="appIcon"
    />
    <div class="page-bottom-filler"></div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import _, { pick } from 'lodash';
import HMHeadingWithButton from '@/web/components/common/HMHeadingWithButton';
import HMFileUpload from '@/web/components/common/HMFileUpload';
import HMButton from '@/web/components/common/HMButton';
import timezones from '@/web/utils/timezones.ts';
import HMCheckBox from '@/web/components/common/HMCheckBox';
import { ValidationObserver } from 'vee-validate';
import languages from '@/web/utils/languages.ts';
import i18n from '@/web/utils/localization';
import { errorToMessage } from '@/core/utils/errors';
import StudentChangePaymentCardModal from '@/web/components/settings/StudentChangePaymentCardModal.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { getConfig } from '@/web/utils/configuration';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import AddToHomeScreen from '@/web/components/pwa/AddToHomeScreen';
import StudentPhoneNumberComponent from '@/web/components/settings/StudentPhoneNumberComponent';
//import { getMessaging, getToken } from 'firebase/messaging';

export default Vue.extend({
  name: 'StudentSettings',
  components: {
    HMHeadingWithButton,
    HMFileUpload,
    HMButton,
    HMCheckBox,
    ValidationObserver,
    NavItem,
    AddToHomeScreen,
    StudentPhoneNumberComponent,
  },
  data() {
    return {
      contactFormOpen: false,
      contactFormModel: null,
      contactFormInProgress: false,
      contactFormError: null,
      studentEmail: null,
      timezones: timezones,
      passwordFormOpen: false,
      newPassword1: null,
      newPassword2: null,
      passwordInProgress: false,
      passwordFormError: null,
      cancelFormError: null,
      notificationFormOpen: false,
      shown: false,
      languages: languages,
      signedContracts: null,
    };
  },
  computed: {
    ...mapGetters('user', ['currentStudioSubscriptions']),
    ...mapState('user', ['purchases', 'punches', 'user']),
    ...mapGetters({
      studio: 'studio/currentStudio',
    }),
    config() {
      return getConfig();
    },
    appIcon() {
      return this.studio?.app_icon?.thumbnail;
    },
    getActivePunches() {
      let activePunches = _.filter(this.punches, { expired: false });
      activePunches = _.map(activePunches, function (punch) {
        punch.expiration_date = moment(punch.expiration).format('YYYY-MM-DD');
        return punch;
      });
      activePunches = _.groupBy(activePunches, 'expiration_date');
      activePunches = _.map(activePunches, function (value, key) {
        return {
          expiration: key,
          number_of_punches: value.length,
          studio_name: value[0].studio_name,
          id: value[0].id,
        };
      });
      return activePunches;
    },
    studentInfoShorthand() {
      const { first_name, last_name, phone } = this.user?.student;
      const { email } = this.user;
      return [`${first_name} ${last_name}`, email, phone]
        .filter(item => item)
        .join(', ');
    },
    eventNotificationsEnabled: {
      get() {
        return this.user.student.event_notifications_enabled;
      },
      set(value) {
        this.$store.dispatch('user/toggleStudentEventNotifications', value);
      },
    },
    emailNotificationsEnabled: {
      get() {
        return this.user.emails_enabled;
      },
      set(value) {
        this.user.emails_enabled = value;
        this.$store.dispatch('user/toggleStudentEmailNotifications', value);
      },
    },
    smsNotificationsEnabled: {
      get() {
        return this.user.student.sms_notifications_enabled;
      },
      async set(value) {
        try {
          this.user.student.sms_notifications_enabled = value;
          await this.$store.dispatch(
            'user/toggleStudentSMSNotifications',
            value
          );
        } catch (error) {
          this.user.student.sms_notifications_enabled = !value;
        }
      },
    },
    emailNotifications30minEnabled: {
      get() {
        return this.user.student.email_30min_event_notifications_enabled;
      },
      set(value) {
        this.$store.dispatch('user/toggleStudentEmail30mins', value);
      },
    },
    emailNotifications1hrEnabled: {
      get() {
        return this.user.student.email_1h_event_notifications_enabled;
      },
      set(value) {
        this.$store.dispatch('user/toggleStudentEmail1hr', value);
      },
    },
    communityDailyDigestEmailEnabled: {
      get() {
        return this.user.student.email_community_daily_digest_enabled;
      },
      set(value) {
        this.$store.dispatch('user/toggleCommunityDailyDigestEmail', value);
      },
    },
    communityChatNotificationsEmailEnabled: {
      get() {
        return this.user.student.email_community_chat_notifications_enabled;
      },
      set(value) {
        this.$store.dispatch(
          'user/toggleCommunityChatNotificationsEmail',
          value
        );
      },
    },
    communityRealtimeMentionsEmailEnabled: {
      get() {
        return this.user.student.email_community_realtime_mentions_enabled;
      },
      set(value) {
        this.$store.dispatch(
          'user/toggleCommunityRealtimeMentionsEmail',
          value
        );
      },
    },
    privatePodcastNotificationsEmailEnabled: {
      get() {
        return this.user.student.email_private_podcast_notifications_enabled;
      },
      set(value) {
        this.$store.dispatch(
          'user/togglePrivatePodcastNotificationsEmail',
          value
        );
      },
    },
    localLanguage() {
      return this.languages.find(lang => lang.value === this.user?.language);
    },
    showUpdatePaymentInfo() {
      return this.currentStudioSubscriptions?.some(
        s => s.subscription_type === 'stripe' && !s.canceled
      );
    },
  },
  methods: {
    openContactForm() {
      this.contactFormOpen = true;
      this.contactFormModel = pick(this.user.student, [
        'first_name',
        'last_name',
        'address',
        'city',
        'state',
        'phone',
        'zipcode',
        'country',
      ]);
      this.studentEmail = this.user.email;
    },
    closeContactForm() {
      this.contactFormOpen = false;
    },
    async updateStudentInfo() {
      this.contactFormInProgress = true;
      const data = { student: this.contactFormModel, email: this.studentEmail };
      try {
        await this.$store.dispatch('user/updateStudent', data);
        this.contactFormOpen = false;
      } catch (error) {
        this.contactFormError = error?.response?.data;
        this.contactFormInProgress = false;
      }
      this.contactFormInProgress = false;
    },
    async setTimezone() {
      try {
        await this.$store.dispatch('user/updateStudent', {
          student: this.user.student,
          timezone: this.user.timezone.value,
        });
      } catch (error) {
        this.formError = error;
      }
    },
    openPasswordForm() {
      this.passwordFormOpen = true;
    },
    closePasswordForm() {
      this.passwordFormOpen = false;
    },
    async updatePassword() {
      this.passwordInProgress = true;
      try {
        this.passwordFormError = null;
        const data = {
          newPassword1: this.newPassword1,
          newPassword2: this.newPassword2,
        };
        const response = await this.$store.dispatch(
          'user/changePassword',
          data
        );
        this.passwordInProgress = false;
        this.passwordFormOpen = false;
        this.$notify({
          group: 'app',
          type: 'success',
          text: response?.detail,
        });
      } catch (error) {
        this.passwordFormError = error?.response?.data?.new_password2?.[0];
        this.passwordInProgress = false;
      }
    },
    async cancelAccount() {
      console.log('How should we proceed here?');
    },
    async cancelSubscription(id) {
      try {
        await this.$store.dispatch('user/cancelStudentSubscription', id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('STUDIO.NOTIFICATIONS.SUBSCRIPTION_CANCEL_SUCCESS'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to cancel subscription',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
      await this.$store.dispatch('user/studentActiveSubscriptions');
    },
    showPayPalInstructions() {
      window.open(this.config.paypalMembershipCancelHelpURL, '_blank');
    },
    getSubscriptionState(subscription) {
      if (subscription.canceled) return 'cancelled';
      if (subscription.paused) return 'paused';
      return 'active';
    },
    openNotificationForm() {
      this.notificationFormOpen = true;
    },
    closeNotificationForm() {
      this.notificationFormOpen = false;
    },
    getLength(arr) {
      return arr?.length;
    },
    async setLanguage(value) {
      try {
        await this.$store.dispatch('user/updateStudent', {
          language: value.value,
          student: {},
        });
        i18n.locale = value.value;
      } catch (error) {
        this.formError = error;
      }
    },
    async updatePaymentInfo() {
      const studioURL = getStudioURL(this.$route);

      const subscription = this.currentStudioSubscriptions?.find(
        s => s.subscription_type === 'stripe' && !s.canceled
      );

      this.$modal.show(StudentChangePaymentCardModal, {
        studioURL: studioURL,
        productId: subscription.product_id,
        cardChangedFn: async payload => {
          const data = {
            id: subscription.id,
            tk: payload.paymentMethod,
            type: 'multi',
          };
          await this.$store.dispatch(
            'user/updateStudentSubscriptionPayment',
            data
          );
        },
      });
    },
  },
  async mounted() {
    this.$store.dispatch('user/studentActiveSubscriptions');
    this.$store.dispatch('user/studentPurchases');
    this.$store.dispatch('user/studentPunches');
    this.signedContracts = await this.$store.dispatch(
      'studio/getSignedContracts',
      { studioURL: getStudioURL(this.$route) }
    );
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.status {
  &--active {
    color: $light-green;
  }
  &--cancelled {
    color: $coral;
  }
  &--paused {
    color: $blue;
  }
  &--trialing {
    color: $light-green;
  }
}
.product-name {
  padding-top: 2px;
}
.cancelled {
  text-decoration: line-through;
}
.table-wrap {
  overflow-x: auto;
}
.cursor-pointer {
  cursor: pointer;
}
.dot {
  margin-right: 6px;
  font-size: 0.5rem;
  &--signed {
    color: $light-green;
  }
  &--pending {
    color: $mustard;
  }
}
</style>
